// Cómo se ve un comentario: hace cuánto se escribió y la inicial de quien lo
// escribió — ADR-2026-09-15-los-comentarios-del-lanzamiento-se-ven-como-los-de-flujos.
//
// Vive acá y no adentro de una de las dos pantallas porque los comentarios de
// Flujos y los de un lanzamiento tienen que decir lo MISMO: si en un lado dice
// "hace 3 d" y en el otro "hace 3 días", son dos productos distintos para el
// mismo usuario. Es la misma razón por la que `etiquetaDeVariante` vive afuera
// de las dos pantallas que la usan.

/**
 * Hace cuánto, en la forma en que uno lo diría.
 *
 * Los saltos son los de Flujos, que es la pantalla de referencia: minutos hasta
 * la hora, horas hasta el día, días hasta la semana, y de ahí en adelante la
 * fecha — a la semana ya no importa el "hace cuánto" sino cuándo fue.
 */
export function cuando(iso: string | undefined | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  // Un reloj adelantado deja fechas "del futuro" por unos segundos: decir
  // "ahora" es preferible a "hace -2 min".
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const dias = Math.floor(h / 24);
  if (dias < 7) return `hace ${dias} d`;
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

/** La letra del avatar. Sin nombre, un signo de pregunta antes que un círculo vacío. */
export const inicialDe = (nombre: string | null | undefined): string =>
  ((nombre ?? '').trim().charAt(0) || '?').toUpperCase();

// ── Arrobar a alguien ──────────────────────────────────────────────────────
//
// Las tres funciones venían de Flujos y se mudaron acá enteras cuando los
// comentarios del lanzamiento las necesitaron: son puras y no saben nada de un
// flujo ni de un producto. `graph/comentarios.ts` las re-exporta.

/** Lo mínimo que hace falta para arrobar a alguien. */
export interface Mencionable {
  id: string;
  nombre: string;
}

/**
 * Escapa lo que en una expresión regular significa otra cosa.
 *
 * Va en una función aparte y NO adentro del template de abajo: ahí estaba, y
 * el `${` de su propio patrón rompía el parseo del template — la clase de
 * caracteres quedaba partida y no escapaba nada. El síntoma era que un nombre
 * con punto funcionaba como comodín: con un autor llamado "matias.perez",
 * escribir "@matiasXperez" lo arrobaba igual. Acá importa más que en Flujos
 * porque cuando alguien no tiene perfil su nombre ES el email, y un email es
 * todo puntos.
 */
export function escaparRegex(texto: string): string {
  return texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Nombres arrobados en un texto ("@Imanol Castro" acepta espacios si el nombre existe). */
export function menciones<T extends Mencionable>(texto: string, participantes: readonly T[]): T[] {
  const encontrados: T[] = [];
  for (const p of participantes) {
    // El corte del final es lo que hace que "@Mati" no arrobe a "Matilde".
    const re = new RegExp(`@${escaparRegex(p.nombre)}(?!\\w)`, 'i');
    if (re.test(texto)) encontrados.push(p);
  }
  return encontrados;
}

/** Si el cursor está escribiendo un "@algo", devuelve el fragmento y las sugerencias. */
export function sugerenciasArroba<T extends Mencionable>(
  texto: string,
  cursor: number,
  participantes: readonly T[],
): { desde: number; fragmento: string; opciones: T[] } | null {
  const antes = texto.slice(0, cursor);
  const m = /(?:^|\s)@([^\s@]*)$/.exec(antes);
  if (!m) return null;
  const fragmento = m[1];
  const desde = antes.length - fragmento.length - 1;
  const q = fragmento.toLowerCase();
  const opciones = participantes.filter((p) => !q || p.nombre.toLowerCase().includes(q)).slice(0, 6);
  return { desde, fragmento, opciones };
}

/** Reemplaza el "@frag" que se está escribiendo por "@Nombre ". */
export function completarArroba(texto: string, cursor: number, nombre: string): { texto: string; cursor: number } {
  const s = sugerenciasArroba(texto, cursor, [{ id: '', nombre }]);
  if (!s) return { texto, cursor };
  const antes = texto.slice(0, s.desde);
  const despues = texto.slice(cursor);
  const insert = `@${nombre} `;
  return { texto: antes + insert + despues, cursor: (antes + insert).length };
}
